import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Badge } from '../ui/badge.tsx';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

type OptimizationPoint = {
  value: number;
  metric: number | null;
};

type OptimizationRange = {
  start?: number;
  end?: number;
  step?: number;
  steps?: number;
};

type OptimizationPayload = {
  variable?: string;
  target?: string;
  direction?: string;
  range?: OptimizationRange;
  points?: OptimizationPoint[];
  best_value?: number | null;
  best_metric?: number | null;
};

interface OptimizationResultsPanelProps {
  result?: {
    optimization?: OptimizationPayload;
  };
}

const formatPercent = (value: number | null | undefined) => {
  if (!Number.isFinite(value as number)) return 'N/A';
  return `${((value as number) * 100).toFixed(2)}%`;
};

const formatNumber = (value: number | null | undefined, digits = 2) => {
  if (!Number.isFinite(value as number)) return 'N/A';
  return (value as number).toFixed(digits);
};

const formatDays = (value: number | null | undefined) => {
  if (!Number.isFinite(value as number)) return 'N/A';
  return `${Math.round(value as number)}d`;
};

const formatParamValue = (value: number | null | undefined) => {
  if (!Number.isFinite(value as number)) return 'N/A';
  const abs = Math.abs(value as number);
  if (abs >= 1000) return (value as number).toFixed(2);
  if (abs > 0 && abs < 0.01) return (value as number).toFixed(6);
  return (value as number).toFixed(4);
};

const TARGET_META: Record<string, { label: string; format: (value: number | null | undefined) => string }> = {
  total_return: { label: 'Total Return', format: formatPercent },
  annualized_return: { label: 'Annualized Return', format: formatPercent },
  volatility: { label: 'Volatility', format: formatPercent },
  sharpe_ratio: { label: 'Sharpe Ratio', format: (v) => formatNumber(v, 2) },
  sortino_ratio: { label: 'Sortino Ratio', format: (v) => formatNumber(v, 2) },
  max_drawdown: { label: 'Max Drawdown', format: formatPercent },
  win_rate: { label: 'Win Rate', format: formatPercent },
  avg_trade_return: { label: 'Avg Trade Return', format: formatPercent },
  information_ratio: { label: 'Information Ratio', format: (v) => formatNumber(v, 2) },
  time_under_water: { label: 'Max Time Under Water', format: formatDays },
  avg_holding_days: { label: 'Avg Holding Days', format: formatDays },
};

const OptimizationResultsPanel: React.FC<OptimizationResultsPanelProps> = ({ result }) => {
  const optimization = result?.optimization;
  const points = useMemo(
    () => (Array.isArray(optimization?.points) ? optimization?.points ?? [] : []),
    [optimization?.points]
  );
  const target = optimization?.target || 'total_return';
  const direction = optimization?.direction || 'max';
  const variable = optimization?.variable || 'x';
  const meta = TARGET_META[target] || { label: target, format: (v: number | null | undefined) => formatNumber(v, 4) };

  const chartData = useMemo(() => {
    const data = points.map((pt) => ({
      value: pt.value,
      metric: Number.isFinite(pt.metric as number) ? pt.metric : null,
    }));
    data.sort((a, b) => a.value - b.value);
    return data;
  }, [points]);

  const bestValue = optimization?.best_value ?? null;
  const bestMetric = optimization?.best_metric ?? null;
  const range = optimization?.range || {};

  return (
    <div className="results-panel">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Optimization Results</CardTitle>
              <div className="mt-1 text-sm text-gray-600">
                Optimizing <span className="font-semibold">{variable}</span> for {meta.label} ({direction === 'min' ? 'minimize' : 'maximize'}).
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{meta.label}</Badge>
              <Badge variant={direction === 'min' ? 'outline' : 'secondary'}>
                {direction === 'min' ? 'Minimize' : 'Maximize'}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Best {variable}</span>
              </div>
              <div className="metric-value">{formatParamValue(bestValue)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Best {meta.label}</span>
              </div>
              <div className="metric-value">{meta.format(bestMetric)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Range</span>
              </div>
              <div className="metric-value">
                {formatParamValue(range.start ?? null)} {'->'} {formatParamValue(range.end ?? null)}
              </div>
              <div className="text-xs text-gray-500">Step {formatParamValue(range.step ?? null)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Steps</span>
              </div>
              <div className="metric-value">{range.steps ?? points.length}</div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 text-sm font-semibold text-gray-700">Optimization Curve</div>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="value"
                    tickFormatter={(v) => formatParamValue(Number(v))}
                    stroke="#6b7280"
                  />
                  <YAxis
                    tickFormatter={(v) => meta.format(Number(v))}
                    stroke="#6b7280"
                  />
                  <Tooltip
                    formatter={(value: any) => meta.format(value as number)}
                    labelFormatter={(label) => `${variable} = ${formatParamValue(Number(label))}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="metric"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={(props: any) => {
                      const isBest = Number.isFinite(bestValue as number) && props?.payload?.value === bestValue;
                      const size = isBest ? 6 : 4;
                      return (
                        <circle
                          cx={props.cx}
                          cy={props.cy}
                          r={size}
                          fill={isBest ? '#10b981' : '#2563eb'}
                          stroke="#ffffff"
                          strokeWidth={1}
                        />
                      );
                    }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
              Results Table
            </div>
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white shadow-sm">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-2">{variable}</th>
                    <th className="px-4 py-2">{meta.label}</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((pt, idx) => {
                    const isBest = Number.isFinite(bestValue as number) && pt.value === bestValue;
                    return (
                      <tr
                        key={`${pt.value}-${idx}`}
                        className={isBest ? 'bg-emerald-50 font-semibold' : 'border-t border-gray-100'}
                      >
                        <td className="px-4 py-2">{formatParamValue(pt.value)}</td>
                        <td className="px-4 py-2">{meta.format(pt.metric)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OptimizationResultsPanel;
